import React, { Component } from "react";
import gql from "graphql-tag";
import { graphql } from "react-apollo";
import { withRouter } from "react-router-dom";
import * as compose from "lodash.flowright";
import * as Cookies from "es-cookie";
import { Row, Col } from "react-bootstrap";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import TabSection from "./TabSection";
import { PageShimmer } from "./Shimmer";

const GET_DOCUMENT_LOGS = gql`
  query getDocumentLogs($student_user_exam_id: Int!) {
    getDocumentLogs(student_user_exam_id: $student_user_exam_id) {
      student_id
      submitted_ids
      submitted_count
      submitted_names
      pending_ids
      pending_count
      pending_names
      returned_ids
      returned_count
      returned_names
      collected_comments
      pending_comments
      return_comments
    }
  }
`;

// ── Field normalisers (backend returns array | JSON string | CSV) ──────────────
const parseNames = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean).map((s) => String(s).trim());
  const s = String(raw).trim();
  if (!s) return [];
  try {
    const parsed = JSON.parse(s);
    if (Array.isArray(parsed)) return parsed.filter(Boolean).map((x) => String(x).trim());
  } catch (e) {
    /* fall through to CSV */
  }
  return s.split(",").map((x) => x.trim()).filter(Boolean);
};

// Each comment field is parallel to its names array ("||"-separated or JSON array).
const parseCommentMap = (raw, docNames = []) => {
  if (raw === null || raw === undefined) return {};
  if (Array.isArray(raw)) {
    const map = {};
    docNames.forEach((name, i) => { if (raw[i]) map[name] = String(raw[i]); });
    return map;
  }
  if (typeof raw === "object") return raw;
  const s = String(raw).trim();
  if (!s) return {};
  try {
    const parsed = JSON.parse(s);
    if (Array.isArray(parsed)) {
      const map = {};
      docNames.forEach((name, i) => { if (parsed[i]) map[name] = String(parsed[i]); });
      return map;
    }
    if (parsed && typeof parsed === "object") return parsed;
  } catch (e) {
    /* fall through to "||" split */
  }
  const parts = s.split("||").map((x) => x.trim());
  const map = {};
  docNames.forEach((name, i) => { if (parts[i]) map[name] = parts[i]; });
  return map;
};

// Status presentation — one entry per document state in the log.
const STATUS_META = {
  submitted: { label: "Submitted", color: "#16a34a", bg: "#f0fdf4", grad: "linear-gradient(135deg,#22c55e,#16a34a)", icon: "fa-check-circle" },
  pending: { label: "Pending", color: "#b45309", bg: "#fef3c7", grad: "linear-gradient(135deg,#f59e0b,#f97316)", icon: "fa-hourglass-half" },
  returned: { label: "Returned", color: "#1d4ed8", bg: "#eff6ff", grad: "linear-gradient(135deg,#3b82f6,#1d4ed8)", icon: "fa-reply" },
};

class DocumentsTab extends Component {
  state = { filter: "all" };

  setFilter = (filter) => this.setState({ filter });

  // Query is keyed by student_user_exam_id, so the response is already this student's record(s).
  studentRecord = () => {
    const raw = this.props.documentLogs?.getDocumentLogs;
    const logs = Array.isArray(raw) ? raw : raw ? [raw] : [];
    return logs[0] || null;
  };

  // Flatten the three name lists into one ordered list of { name, status, comment }.
  buildDocuments = (record) => {
    if (!record) return [];
    const submitted = parseNames(record.submitted_names);
    const pending = parseNames(record.pending_names);
    const returned = parseNames(record.returned_names);

    const collectedMap = parseCommentMap(record.collected_comments, submitted);
    const pendingMap = parseCommentMap(record.pending_comments, pending);
    const returnMap = parseCommentMap(record.return_comments, returned);

    return [
      ...submitted.map((name) => ({ name, status: "submitted", comment: collectedMap[name] || "" })),
      ...returned.map((name) => ({ name, status: "returned", comment: returnMap[name] || "" })),
      ...pending.map((name) => ({ name, status: "pending", comment: pendingMap[name] || "" })),
    ];
  };

  counts = (record) => ({
    submitted: record.submitted_count ?? parseNames(record.submitted_names).length,
    pending: record.pending_count ?? parseNames(record.pending_names).length,
    returned: record.returned_count ?? parseNames(record.returned_names).length,
  });

  // Animated KPI cards for each document state.
  renderCounts = (c) => {
    const items = [
      { key: "submitted", count: c.submitted },
      { key: "pending", count: c.pending },
      { key: "returned", count: c.returned },
    ];
    return (
      <Row>
        {items.map(({ key, count }, i) => {
          const meta = STATUS_META[key];
          return (
            <Col md={4} sm={12} key={key} className="mb-3">
              <div className="sp-af-card sp-animate d-flex align-items-center justify-content-between p-3 h-100" style={{ animationDelay: `${i * 80}ms` }}>
                <div>
                  <span className="d-block text-muted" style={{ fontSize: 13, fontWeight: 600 }}>{meta.label}</span>
                  <strong className="d-block text-dark" style={{ fontSize: "2rem", fontFamily: "'Outfit', sans-serif", lineHeight: 1.2 }}>{count}</strong>
                </div>
                <div className="d-inline-flex align-items-center justify-content-center text-white flex-shrink-0"
                  style={{ width: 46, height: 46, borderRadius: 12, background: meta.grad, boxShadow: "0 6px 14px rgba(0,0,0,0.14)" }}>
                  <i className={`fas ${meta.icon}`} style={{ fontSize: 18 }} />
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
    );
  };

  // Donut of the submitted/returned/pending split.
  renderDonut = (c, completion) => {
    const options = {
      chart: { type: "pie", backgroundColor: "transparent", height: 210 },
      title: {
        text: `<div style="text-align:center"><span style="font-size:24px;font-weight:700;color:#16a34a">${completion}%</span><br/><span style="font-size:10px;color:#94a3b8">COMPLETE</span></div>`,
        align: "center", verticalAlign: "middle", y: 6, useHTML: true,
      },
      credits: { enabled: false },
      tooltip: { enabled: true, pointFormat: "<b>{point.y}</b> docs" },
      plotOptions: { pie: { innerSize: "72%", borderWidth: 0, dataLabels: { enabled: true, format: "{point.name}: {point.y}", style: { fontWeight: "700", color: "#334155" } } } },
      series: [
        {
          animation: { duration: 900 },
          data: [
            { name: "Submitted", y: c.submitted, color: "#22c55e" },
            { name: "Returned", y: c.returned, color: "#3b82f6" },
            { name: "Pending", y: c.pending, color: "#f59e0b" },
          ].filter((d) => d.y > 0),
        },
      ],
    };
    return <HighchartsReact highcharts={Highcharts} options={options} />;
  };

  renderControls = (c) => {
    const { filter } = this.state;
    const chips = [
      { key: "all", label: `All (${c.submitted + c.pending + c.returned})` },
      { key: "submitted", label: `Submitted (${c.submitted})` },
      { key: "returned", label: `Returned (${c.returned})` },
      { key: "pending", label: `Pending (${c.pending})` },
    ];
    return (
      <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: 10 }}>
        {chips.map((ch) => (
          <button key={ch.key} type="button" className={`sp-filter-chip ${filter === ch.key ? "active" : ""}`} onClick={() => this.setFilter(ch.key)}>
            {ch.label}
          </button>
        ))}
      </div>
    );
  };

  renderCard = (doc, idx) => {
    const meta = STATUS_META[doc.status];
    return (
      <Col md={6} key={`${doc.status}-${doc.name}-${idx}`} className="mb-3">
        <div className="sp-doc-card sp-animate d-flex align-items-center justify-content-between" style={{ borderLeftColor: meta.color, animationDelay: `${Math.min(idx, 14) * 50}ms` }}>
          <div className="d-flex align-items-center" style={{ gap: 14, minWidth: 0 }}>
            <div className="sp-doc-ico flex-shrink-0" style={{ background: meta.bg, color: meta.color }}>
              <i className="fas fa-file-alt" />
            </div>
            <div style={{ minWidth: 0 }}>
              <span className="d-block font-weight-bold text-dark text-truncate" style={{ fontSize: 14 }}>{doc.name}</span>
              {doc.comment ? (
                <span className="d-block text-muted text-truncate" style={{ fontSize: 12 }} title={doc.comment}>
                  <i className="far fa-comment-dots mr-1" />{doc.comment}
                </span>
              ) : (
                <span className="d-block text-muted" style={{ fontSize: 12 }}>{meta.label}</span>
              )}
            </div>
          </div>
          <span className="sp-status-pill flex-shrink-0 ml-2" style={{ background: meta.bg, color: meta.color }}>
            <i className={`fas ${meta.icon}`} style={{ fontSize: 11 }} />{meta.label}
          </span>
        </div>
      </Col>
    );
  };

  renderBody = () => {
    const { student } = this.props;
    const query = this.props.documentLogs || {};

    if (query.loading) return <PageShimmer />;

    if (query.error) {
      return (
        <div className="text-center py-5" style={{ color: "#dc2626" }}>
          <i className="fas fa-exclamation-circle d-block mb-2" style={{ fontSize: 30 }} />
          <div style={{ fontSize: 14 }}>Failed to load documents. {query.error.message}</div>
        </div>
      );
    }

    const record = this.studentRecord();
    const documents = this.buildDocuments(record);

    if (!record || documents.length === 0) {
      return (
        <div className="text-center text-muted py-5">
          <i className="far fa-folder-open d-block mb-2" style={{ fontSize: 34, color: "#cbd5e1" }} />
          <div style={{ fontSize: 14 }}>No document records found for {student?.student_name || "this student"}.</div>
        </div>
      );
    }

    const c = this.counts(record);
    const total = c.submitted + c.pending + c.returned;
    const completion = total ? Math.round((c.submitted / total) * 100) : 0;
    const filtered = this.state.filter === "all" ? documents : documents.filter((d) => d.status === this.state.filter);

    return (
      <div>
        {this.renderCounts(c)}

        <Row className="mb-4">
          <Col lg={8} className="mb-3 mb-lg-0">
            <div className="sp-af-card sp-animate p-4 h-100 d-flex flex-column justify-content-center" style={{ animationDelay: "120ms" }}>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="font-weight-bold text-dark mb-0" style={{ fontFamily: "'Outfit', sans-serif" }}>Document Completion</h6>
                <span className="font-weight-bold" style={{ color: "#16a34a", fontSize: 15 }}>{completion}%</span>
              </div>
              <div className="sp-progress-track">
                <div className="sp-progress-fill" style={{ width: `${completion}%` }} />
              </div>
              <div className="d-flex justify-content-between mt-2 text-muted" style={{ fontSize: 12 }}>
                <span><i className="fas fa-check-circle mr-1" style={{ color: "#22c55e" }} />{c.submitted} submitted</span>
                <span><i className="fas fa-reply mr-1" style={{ color: "#3b82f6" }} />{c.returned} returned</span>
                <span><i className="fas fa-hourglass-half mr-1" style={{ color: "#f59e0b" }} />{c.pending} pending</span>
              </div>
            </div>
          </Col>
          <Col lg={4}>
            <div className="sp-af-card sp-animate p-3 h-100" style={{ animationDelay: "180ms" }}>
              {this.renderDonut(c, completion)}
            </div>
          </Col>
        </Row>

        <div className="sp-af-card p-4">
          <h6 className="font-weight-bold text-dark mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Documents</h6>
          {this.renderControls(c)}
          {filtered.length > 0 ? (
            <Row>{filtered.map(this.renderCard)}</Row>
          ) : (
            <div className="text-center text-muted py-4" style={{ fontSize: 14 }}>No documents in this category.</div>
          )}
        </div>
      </div>
    );
  };

  render() {
    return (
      <TabSection
        icon="fa-id-card"
        title="Documents"
        desc="Submitted, pending and returned admission documents."
      >
        {this.renderBody()}
      </TabSection>
    );
  }
}

export default withRouter(
  compose(
    graphql(GET_DOCUMENT_LOGS, {
      name: "documentLogs",
      options: () => ({
        variables: {
          student_user_exam_id: parseInt(Cookies.get("student_user_exam_id")),
        },
        fetchPolicy: "network-only",
      }),
    })
  )(DocumentsTab)
);
